// src/pages/Automations/components/OEPSection.jsx
// Dedicated OEP section with timeline visualization (Light theme)
// Updated: All OEP automations send at 1 PM Eastern (no more per-agent send hours)

import React from 'react';
import { RefreshCw, Calendar, Edit2, Check, Clock } from 'lucide-react';

const OEP_TRIGGERS = [
  {
    key: 'oep_jan2',
    name: 'January Check-in',
    date: 'January 2',
    description: 'New year greeting + plan working check',
    month: 1
  },
  {
    key: 'oep_feb1',
    name: 'February Follow-up',
    date: 'February 1',
    description: 'Mid-OEP check on plan satisfaction',
    month: 2
  },
  {
    key: 'oep_mar1',
    name: 'March Deadline',
    date: 'March 1',
    description: 'Final OEP reminder - deadline March 31',
    month: 3
  }
];

export function OEPSection({ isEnabled, onToggle, onEditTemplate, templates }) {
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentDay = new Date().getDate();
  const isOEPSeason = currentMonth >= 1 && currentMonth <= 3;

  // Helper: check if a trigger date has passed (only relevant during OEP season Jan-Mar)
  const hasPassed = (triggerMonth) => {
    if (!isOEPSeason) return false;
    // Special case: Jan 2 - check if we're past Jan 2
    if (triggerMonth === 1) {
      return currentMonth > 1 || (currentMonth === 1 && currentDay > 2);
    }
    return currentMonth > triggerMonth;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-t-4 border-amber-500 overflow-hidden">
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <RefreshCw className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-800">OEP Automations</h3>
              {isOEPSeason && (
                <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                  ACTIVE SEASON
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Open Enrollment Period outreach (January 1 - March 31)
            </p>
          </div>
        </div>

      </div>

      {/* Timeline */}
      <div className="px-5 sm:px-6 py-6">
        {/* Timeline Visual */}
        <div className="relative mb-8">
          {/* Line */}
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
            <div
              className="h-full bg-amber-500 transition-all duration-500"
              style={{
                width: isOEPSeason
                  ? `${((currentMonth - 1) / 2) * 100}%`
                  : '0%'
              }}
            />
          </div>

          {/* Points */}
          <div className="relative flex justify-between">
            {OEP_TRIGGERS.map((trigger, idx) => {
              const isPast = hasPassed(trigger.month);
              const isCurrent = currentMonth === trigger.month;
              const enabled = isEnabled(trigger.key);

              return (
                <div key={trigger.key} className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                      isPast
                        ? 'bg-green-500 border-green-500'
                        : isCurrent
                        ? 'bg-amber-500 border-amber-500 ring-4 ring-amber-200'
                        : enabled
                        ? 'bg-white border-amber-500'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {isPast ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : (
                      <span className={`text-xs font-bold ${isCurrent ? 'text-white' : 'text-gray-600'}`}>{idx + 1}</span>
                    )}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${
                    isCurrent ? 'text-amber-600' : 'text-gray-500'
                  }`}>
                    {trigger.date}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {OEP_TRIGGERS.map((trigger) => {
            const enabled = isEnabled(trigger.key);
            const isPast = hasPassed(trigger.month);
            const isCurrent = currentMonth === trigger.month;

            return (
              <div
                key={trigger.key}
                className={`p-4 rounded-xl border transition-all ${
                  isCurrent
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Calendar className={`w-4 h-4 ${isCurrent ? 'text-amber-600' : 'text-gray-400'}`} />
                      <span className={`text-xs font-medium ${isCurrent ? 'text-amber-600' : 'text-gray-500'}`}>
                        {trigger.date}
                      </span>
                      {isPast && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          Sent
                        </span>
                      )}
                    </div>
                    <h4 className="text-gray-800 font-semibold mt-1">{trigger.name}</h4>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-500 mb-3">{trigger.description}</p>

                {/* Send Time (Fixed - 1 PM Eastern) */}
                <div className="flex items-center gap-2 mb-4 p-2 bg-white rounded-lg border border-gray-200">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs text-gray-600">Sends at:</span>
                  <span className="text-sm text-gray-800 font-medium">1:00 PM Eastern</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => onEditTemplate({ key: trigger.key, name: trigger.name, explanation: trigger.description, channels: ['sms', 'email'] })}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm rounded-lg transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>

                  {/* Toggle */}
                  <button
                    onClick={() => onToggle(trigger.key, !enabled)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={enabled}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                        enabled ? 'left-6' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note about Spanish */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-gray-700">
            <span className="font-medium text-blue-700">Spanish Support:</span> OEP automations automatically send messages in Spanish for clients with Spanish as their preferred language.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OEPSection;
